/**
 * CasperBridge — типизированный клиент для взаимодействия с нативным Unity/UniWebView.
 * Bridge устанавливается нативным приложением в window.CasperBridge до загрузки страницы.
 *
 * Все нативные вызовы проходят через bridgeQueue (lib/casper-bridge-manager.ts):
 * — строго последовательно (никаких параллельных запросов)
 * — с таймаутом 15 с (BRIDGE_TIMEOUT_MS) или 5 мин для действий пользователя
 * — с полным логированием аргументов и результатов в консоль
 *
 * Документация: docs/API/CASPER_BRIDGE.md
 */
import { bridgeQueue, BRIDGE_USER_ACTION_TIMEOUT_MS } from '@/lib/casper-bridge-manager'

// ─── Результаты методов ───────────────────────────────────────────────────────

export interface CasperBridgeInitResult {
  initialized: boolean
  apiUrl: string
  webUrl: string
}

export interface CasperBridgeVersionResult {
  version: string
  buildNumber: string
}

export interface CasperBridgeLoginResult {
  userId: string | number
  loggedIn: boolean
  deviceRegistered: boolean
}

export interface CasperBridgeLogoutResult {
  userId: string | number
  loggedIn: boolean
  deviceDeleted: boolean
}

export interface CasperBridgeAROpenResult {
  opened: boolean
}

export interface CasperBridgeARCloseResult {
  opened: boolean
}

export interface CasperBridgeCameraAttachment {
  url: string
  mediaType: "image" | "video"
}

export interface CasperBridgeCameraResult {
  attachment: CasperBridgeCameraAttachment
}

export interface CasperBridgeFilePickerAttachment {
  url: string
}

export interface CasperBridgeFilePickerResult {
  attachment: CasperBridgeFilePickerAttachment
}

/** Типы файлов для FilePicker.pick() (битовые флаги). */
export const CasperBridgeFileType = {
  Image: 1,
  Video: 2,
  ImageAndVideo: 3,
} as const

// ─── Нативный интерфейс ───────────────────────────────────────────────────────

interface CasperBridgeNative {
  init(): Promise<CasperBridgeInitResult>
  getVersion(): Promise<CasperBridgeVersionResult>
  login(token: string, userId: string | number): Promise<CasperBridgeLoginResult>
  logout(userId: string | number): Promise<CasperBridgeLogoutResult>
  AR: {
    open(): Promise<CasperBridgeAROpenResult>
    close(): Promise<CasperBridgeARCloseResult>
  }
  Camera: {
    record(): Promise<CasperBridgeCameraResult>
  }
  FilePicker: {
    FileType: typeof CasperBridgeFileType
    pick(fileType: number): Promise<CasperBridgeFilePickerResult>
  }
  System: {
    setStartUrl(url: string): Promise<void>
    getStartUrl(): Promise<{ url: string; forced: boolean }>
    clearStartUrl(): Promise<{ cleared: boolean }>
    setApiUrl(apiUrl: string): Promise<{ apiUrl: string; changed: boolean }>
    getApiUrl(): Promise<{ apiUrl: string }>
    clearApiUrl(): Promise<{ apiUrl: string; cleared: boolean }>
  }
}

declare global {
  interface Window {
    CasperBridge?: CasperBridgeNative
  }
}

// ─── Утилиты ─────────────────────────────────────────────────────────────────

export function isCasperBridgeAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.CasperBridge
}

// ─── Публичные методы ─────────────────────────────────────────────────────────

/**
 * Инициализирует Bridge и возвращает конфигурацию приложения (apiUrl, webUrl).
 * Возвращает null если Bridge недоступен или произошла ошибка.
 */
export async function casperBridgeInit(): Promise<CasperBridgeInitResult | null> {
  if (!isCasperBridgeAvailable()) return null
  try {
    return await bridgeQueue.enqueue('init', [], () => window.CasperBridge!.init())
  } catch {
    return null
  }
}

/**
 * Возвращает версию и номер сборки нативного приложения.
 * Возвращает null если Bridge недоступен или произошла ошибка.
 */
export async function casperBridgeGetVersion(): Promise<CasperBridgeVersionResult | null> {
  if (!isCasperBridgeAvailable()) return null
  try {
    return await bridgeQueue.enqueue('getVersion', [], () => window.CasperBridge!.getVersion())
  } catch {
    return null
  }
}

/**
 * Передаёт токен и userId в нативное приложение для создания авторизованной сессии
 * и регистрации push-токена устройства. Должен вызываться после каждого успешного
 * входа в веб-части (login, photo-auth, add-account).
 */
export async function casperBridgeLogin(
  token: string,
  userId: string | number,
): Promise<CasperBridgeLoginResult | null> {
  if (!isCasperBridgeAvailable()) return null
  try {
    const result = await bridgeQueue.enqueue(
      'login',
      [`userId=${userId}`, `token(last16)=…${token.slice(-16)}`],
      () => window.CasperBridge!.login(token, userId),
    )
    if (result && !result.deviceRegistered) {
      console.warn('[Bridge] login: push-token не зарегистрирован (ожидаемо в Unity Editor)')
    }
    return result
  } catch {
    return null
  }
}

/**
 * Удаляет нативную сессию и регистрацию push-устройства.
 * Должен вызываться при любом выходе: явный logout, истечение токена, 401.
 */
export async function casperBridgeLogout(
  userId: string | number,
): Promise<CasperBridgeLogoutResult | null> {
  if (!isCasperBridgeAvailable()) return null
  try {
    return await bridgeQueue.enqueue(
      'logout',
      [`userId=${userId}`],
      () => window.CasperBridge!.logout(userId),
    )
  } catch {
    return null
  }
}

// ─── AR ───────────────────────────────────────────────────────────────────────

/**
 * Открывает AR-сцену (Main).
 * Перед вызовом должен быть выполнен успешный casperBridgeLogin().
 * Возвращает null если Bridge недоступен или произошла ошибка.
 */
export async function casperBridgeAROpen(): Promise<CasperBridgeAROpenResult | null> {
  if (!isCasperBridgeAvailable()) return null
  try {
    return await bridgeQueue.enqueue('AR.open', [], () => window.CasperBridge!.AR.open())
  } catch {
    return null
  }
}

/**
 * Закрывает AR-сцену и возвращает WebView.
 * Возвращает null если Bridge недоступен или произошла ошибка.
 */
export async function casperBridgeARClose(): Promise<CasperBridgeARCloseResult | null> {
  if (!isCasperBridgeAvailable()) return null
  try {
    return await bridgeQueue.enqueue('AR.close', [], () => window.CasperBridge!.AR.close())
  } catch {
    return null
  }
}

// ─── System ───────────────────────────────────────────────────────────────────

/**
 * Сохраняет новый start URL в нативном приложении.
 * При следующем запуске WebView откроется по этому адресу.
 * Используется только в режиме разработчика.
 */
export async function casperBridgeSetStartUrl(url: string): Promise<void> {
  if (!isCasperBridgeAvailable()) {
    console.warn('[Bridge] System.setStartUrl: CasperBridge недоступен')
    return
  }
  try {
    await bridgeQueue.enqueue(
      'System.setStartUrl',
      [`url=${url}`],
      () => window.CasperBridge!.System.setStartUrl(url),
    )
  } catch (err) {
    throw err
  }
}

/** Сохранённый стартовый адрес WebView. В браузере без оболочки — null. */
export async function casperBridgeGetStartUrl(): Promise<{ url: string; forced: boolean } | null> {
  if (!isCasperBridgeAvailable()) return null
  try {
    return await bridgeQueue.enqueue('System.getStartUrl', [], () => window.CasperBridge!.System.getStartUrl())
  } catch {
    return null
  }
}

/**
 * Сохраняет и применяет пользовательский API URL.
 * При смене адреса очищает кеш ассетов — перед следующим обращением к ассетам
 * необходимо снова вызвать Assets.init(token).
 */
export async function casperBridgeSetApiUrl(apiUrl: string): Promise<{ apiUrl: string; changed: boolean } | null> {
  if (!isCasperBridgeAvailable()) {
    console.warn('[Bridge] System.setApiUrl: CasperBridge недоступен')
    return null
  }
  try {
    return await bridgeQueue.enqueue(
      'System.setApiUrl',
      [`apiUrl=${apiUrl}`],
      () => window.CasperBridge!.System.setApiUrl(apiUrl),
    )
  } catch (err) {
    throw err
  }
}

/**
 * Возвращает активный API URL с учётом сохранённого пользовательского значения.
 */
export async function casperBridgeGetApiUrl(): Promise<{ apiUrl: string } | null> {
  if (!isCasperBridgeAvailable()) return null
  try {
    return await bridgeQueue.enqueue('System.getApiUrl', [], () => window.CasperBridge!.System.getApiUrl())
  } catch {
    return null
  }
}

/**
 * Удаляет сохранённый пользовательский API URL и восстанавливает адрес из NetConfig.
 */
export async function casperBridgeClearApiUrl(): Promise<{ apiUrl: string; cleared: boolean } | null> {
  if (!isCasperBridgeAvailable()) {
    console.warn('[Bridge] System.clearApiUrl: CasperBridge недоступен')
    return null
  }
  try {
    return await bridgeQueue.enqueue(
      'System.clearApiUrl',
      [],
      () => window.CasperBridge!.System.clearApiUrl(),
    )
  } catch (err) {
    throw err
  }
}

// ─── Camera ───────────────────────────────────────────────────────────────────

/**
 * Открывает нативный интерфейс камеры для съёмки фото или видео.
 * При отмене пользователем возвращает null (код camera_cancelled).
 * При других ошибках логирует и возвращает null.
 */
export async function casperBridgeCameraRecord(): Promise<CasperBridgeCameraResult | null> {
  if (!isCasperBridgeAvailable()) {
    console.warn('[Bridge] Camera.record: CasperBridge недоступен (не нативный WebView)')
    return null
  }

  // Показываем оверлей до вызова — Camera.record() «замораживает» JS-поток WebView,
  // React не успевает отрендерить оверлей если вызов происходит раньше перерисовки.
  window.dispatchEvent(new CustomEvent('casper:camera-loading', { detail: { active: true } }))
  await new Promise<void>(resolve => setTimeout(resolve, 100))

  let result: CasperBridgeCameraResult
  try {
    result = await bridgeQueue.enqueue(
      'Camera.record',
      [],
      () => window.CasperBridge!.Camera.record(),
      BRIDGE_USER_ACTION_TIMEOUT_MS,
    )
  } catch (err) {
    const e = err as Record<string, unknown>
    if ((e?.code as string) === 'camera_cancelled') {
      console.log('[Bridge] Camera.record: пользователь отменил съёмку (camera_cancelled)')
    }
    window.dispatchEvent(new CustomEvent('casper:camera-loading', { detail: { active: false } }))
    return null
  }

  window.dispatchEvent(new CustomEvent('casper:camera-loading', { detail: { active: false } }))

  // Debug-событие для CameraDebugOverlay (только когда включена настройка)
  try {
    if (typeof window !== 'undefined' && localStorage.getItem('mobile-camera-debug-enabled') === '1') {
      window.dispatchEvent(new CustomEvent('casper:camera-debug-record', {
        detail: {
          timestamp: new Date().toISOString(),
          resultJson: (() => { try { return JSON.stringify(result, null, 2) } catch { return String(result) } })(),
          url: result?.attachment?.url ?? '',
          mediaType: result?.attachment?.mediaType ?? '',
        },
      }))
    }
  } catch { /* никогда не ломаем основной поток */ }

  return result
}

// ─── FilePicker ───────────────────────────────────────────────────────────────

/**
 * Открывает нативный файловый пикер.
 * @param fileType — битовая маска из CasperBridgeFileType (по умолчанию ImageAndVideo = 3)
 * При отмене пользователем возвращает null (код file_picker_cancelled).
 * При других ошибках логирует и возвращает null.
 */
export async function casperBridgeFilePick(
  fileType: number = CasperBridgeFileType.ImageAndVideo,
): Promise<CasperBridgeFilePickerResult | null> {
  if (!isCasperBridgeAvailable()) return null

  try {
    return await bridgeQueue.enqueue(
      'FilePicker.pick',
      [`fileType=${fileType}`],
      () => window.CasperBridge!.FilePicker.pick(fileType),
      BRIDGE_USER_ACTION_TIMEOUT_MS,
    )
  } catch (err) {
    const e = err as Record<string, unknown>
    if ((e?.code as string) === 'file_picker_cancelled') {
      console.log('[Bridge] FilePicker.pick: пользователь отменил выбор (file_picker_cancelled)')
    }
    return null
  }
}

// ─── Утилита: локальный URL → File ────────────────────────────────────────────

/**
 * Нормализует MIME-subtype в правильное файловое расширение.
 * Некоторые MIME-типы имеют subtype, не совпадающий с расширением файла
 * (например video/quicktime → .mov, image/jpeg → .jpg).
 */
const MIME_SUBTYPE_TO_EXT: Record<string, string> = {
  quicktime: 'mov',
  jpeg: 'jpg',
}

export function mimeSubtypeToExt(subtype: string): string {
  return MIME_SUBTYPE_TO_EXT[subtype] ?? subtype
}

/**
 * Загружает файл по локальному HTTP URL (выданному Bridge) и создаёт из него File-объект.
 * Content-Type берётся из заголовков ответа.
 * Возвращает null при ошибке.
 *
 * На iOS локальный HTTP-сервер CasperBridge может не успеть зарегистрировать файл
 * к моменту первого fetch после Camera.record() — отсюда «TypeError: Load failed»
 * примерно в половине случаев. Решение — retry с нарастающей задержкой.
 */
export async function casperBridgeUrlToFile(
  url: string,
  fallbackName?: string,
): Promise<File | null> {
  const MAX_ATTEMPTS = 4
  const RETRY_DELAYS_MS = [150, 400, 800] // паузы между попытками 1→2, 2→3, 3→4

  console.group('[CasperBridge] urlToFile → CALL')
  console.log('timestamp:', new Date().toISOString())
  console.log('url:', url)
  console.log('fallbackName:', fallbackName ?? '(auto)')
  console.log('maxAttempts:', MAX_ATTEMPTS)
  console.groupEnd()

  // Накапливаем данные для debug-оверлея
  const dbgAttempts: Array<{ attempt: number; status?: number; statusText?: string; error?: string }> = []
  let dbgSucceeded = false
  let dbgBlobObjectUrl: string | undefined
  let dbgBlobType: string | undefined
  let dbgBlobSize: number | undefined
  let dbgLastStatus: number | undefined
  let dbgLastStatusText: string | undefined
  let dbgAllHeaders: Record<string, string> | undefined
  let dbgLastError: string | undefined
  let resultFile: File | null = null

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (attempt > 1) {
      const delay = RETRY_DELAYS_MS[attempt - 2]
      console.log(`[CasperBridge] urlToFile: retry attempt ${attempt}/${MAX_ATTEMPTS}, waiting ${delay}ms...`)
      await new Promise(resolve => setTimeout(resolve, delay))
    }

    try {
      console.log(`[CasperBridge] urlToFile: fetch (attempt ${attempt}/${MAX_ATTEMPTS}) →`, url)
      const response = await fetch(url)

      const allHeaders: Record<string, string> = {}
      response.headers.forEach((v, k) => { allHeaders[k] = v })
      dbgLastStatus = response.status
      dbgLastStatusText = response.statusText
      dbgAllHeaders = allHeaders

      console.group(`[CasperBridge] urlToFile → FETCH RESPONSE (attempt ${attempt})`)
      console.log('timestamp:', new Date().toISOString())
      console.log('url:', url)
      console.log('ok:', response.ok)
      console.log('status:', response.status)
      console.log('statusText:', response.statusText)
      console.log('headers:', allHeaders)
      console.log('content-type:', response.headers.get('content-type'))
      console.log('content-length:', response.headers.get('content-length'))
      console.groupEnd()

      if (!response.ok) {
        console.group(`[CasperBridge] urlToFile → HTTP ERROR (attempt ${attempt})`)
        console.error('status:', response.status, response.statusText)
        console.error('url:', url)
        console.error('all headers:', allHeaders)
        console.groupEnd()
        dbgAttempts.push({ attempt, status: response.status, statusText: response.statusText })
        dbgLastError = `HTTP ${response.status} ${response.statusText}`
        throw new Error(`HTTP ${response.status} ${response.statusText}`)
      }

      console.log('[CasperBridge] urlToFile: читаем blob...')
      const blob = await response.blob()

      const mimeType = blob.type || response.headers.get('content-type') || ''
      const isVideo = mimeType.startsWith('video/')
      const rawExt = mimeType.split('/')[1]?.split(';')[0] ?? ''
      const ext = mimeSubtypeToExt(rawExt) || (isVideo ? 'mp4' : 'jpg')
      const name = fallbackName ?? `media_${Date.now()}.${ext}`
      const finalMime = mimeType || (isVideo ? 'video/mp4' : 'image/jpeg')

      console.group('[CasperBridge] urlToFile → BLOB')
      console.log('timestamp:', new Date().toISOString())
      console.log('blob.type:', blob.type)
      console.log('blob.size (bytes):', blob.size)
      console.log('resolved mimeType:', mimeType)
      console.log('isVideo:', isVideo)
      console.log('ext:', ext)
      console.log('finalMime:', finalMime)
      console.groupEnd()

      const file = new File([blob], name, { type: finalMime })

      console.group('[CasperBridge] urlToFile → FILE CREATED')
      console.log('timestamp:', new Date().toISOString())
      console.log('succeededOnAttempt:', attempt)
      console.log('file.name:', file.name)
      console.log('file.type:', file.type)
      console.log('file.size (bytes):', file.size)
      console.log('file.lastModified:', file.lastModified)
      console.groupEnd()

      // Debug данные (успех)
      dbgAttempts.push({ attempt, status: response.status, statusText: response.statusText })
      dbgSucceeded = true
      dbgBlobObjectUrl = URL.createObjectURL(blob)
      dbgBlobType = blob.type
      dbgBlobSize = blob.size
      resultFile = file
      break

    } catch (err) {
      const isNetworkError = (err as Error)?.name === 'TypeError'
      const isLastAttempt = attempt === MAX_ATTEMPTS

      console.group(`[CasperBridge] urlToFile → EXCEPTION (attempt ${attempt}/${MAX_ATTEMPTS})`)
      console.error('timestamp:', new Date().toISOString())
      console.error('url:', url)
      console.error('raw error:', err)
      console.error('error.message:', (err as Error)?.message)
      console.error('error.name:', (err as Error)?.name)
      console.error('String(err):', String(err))
      console.error('isNetworkError (will retry):', isNetworkError && !isLastAttempt)
      console.groupEnd()

      // Добавляем только если ещё не добавили (HTTP ошибки добавляются выше)
      if (isNetworkError) {
        dbgAttempts.push({ attempt, error: String(err) })
      }
      dbgLastError = String(err)

      if (!isNetworkError || isLastAttempt) {
        console.error(`[CasperBridge] urlToFile: все ${attempt} попыток исчерпаны, возвращаем null`)
        break
      }
    }
  }

  // Dispatch debug-событие для CameraDebugOverlay
  try {
    if (typeof window !== 'undefined' && localStorage.getItem('mobile-camera-debug-enabled') === '1') {
      window.dispatchEvent(new CustomEvent('casper:camera-debug-fetch', {
        detail: {
          timestamp: new Date().toISOString(),
          url,
          totalAttempts: dbgAttempts.length,
          succeeded: dbgSucceeded,
          blobObjectUrl: dbgBlobObjectUrl,
          blobType: dbgBlobType,
          blobSize: dbgBlobSize,
          lastStatus: dbgLastStatus,
          lastStatusText: dbgLastStatusText,
          allHeaders: dbgAllHeaders,
          lastError: dbgLastError,
          attempts: dbgAttempts,
        },
      }))
    }
  } catch { /* никогда не ломаем основной поток */ }

  return resultFile
}
